import { ExportGapFilterType, MembershipType, NetworkProviderFilterType } from "@app/exports/enumerations";
import { ExportImpactProviderTypeEnum } from "@app/exports/exportModels";
import ResourceFiltrationService, { FilterItemsGroupModel } from "@app/projects/resource-filtration/resourceFiltrationService";
import { FilterItemsGroupOperation } from "@app/resource-filters/common";
import { FilterItem, FilterItemsGroup } from "@app/resource-filters/filter-items";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import moment from "moment";
import React, { createContext, JSX, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CountiesFilterRadioOptionsType,
  ReportFilterOptionsType,
  ReportFormatOptionsType,
  TagLookup,
  VolumeReportsProject,
  VolumeReportsSpecialty,
  VolumeReportsState,
  VolumeReportsWizardContextSteps,
  reportFormatOptions
} from "./interfaces";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ColumnConfigurationPresetOption } from "@app/report-customization/build-reports/interfaces/columnConfigurationPresetOption";
import { defaultAllColumns } from "@app/report-customization/build-reports/defaultPresetValue";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import ExportService from "@app/exports/exportService";
import ZipProjectService from "@app/projects/zipProjectService";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { FilterSetItem } from "@app/resource-filters/filter-sets/filterSetItem";
import { ResourceFilterSet } from "@app/resource-filters/filter-sets/resourceFilterSet";
import { QESButton } from "@app/shared/qes-standards/Button";
import { scrubUserInput } from "@app/shared/utility/scrubUserInput";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";

export type VolumeReportType = "specialty" | "providers" | "zip-summary" | "specialty-summary-zip-code" | "zip-class-aggregate" | "zip-providers" | "pci" | "countyaggregate" | "stateaggregate" | "specialtyaggregate" | "cciio" | "cciioDataValidations" | "cciioDataValidationsSummary" | "actionable-provider";

export const volumeReportTypeToReportTypeEnumMap = new Map<string, ReportType>([
  ["specialty", ReportType.SpecialtySummaryByCounty],
  ["providers", ReportType.AdequacyServicingProvider],
  ["zip-summary", ReportType.ZipSummary],
  ["specialty-summary-zip-code", ReportType.ZipAnalysisResultsReport],
  ["countyaggregate", ReportType.CountySummary],
  ["stateaggregate", ReportType.StateSummary],
  ["specialtyaggregate", ReportType.AdequacyAggregate],
  ["zip-class-aggregate", ReportType.ZipClassAggregateResultsReport],
  ["zip-providers", ReportType.ZipAnalysisAdequacyServicingProvider],
]);

interface VolumeReportsProps {
  reportType: VolumeReportType;
}

interface VolumeReportsContextProps {
  // meta
  loading: boolean;
  page: VolumeReportsWizardContextSteps;
  reportType: VolumeReportType;
  setPage: React.Dispatch<React.SetStateAction<VolumeReportsWizardContextSteps>>;
  showChangeWarning: boolean;
  setShowChangeWarning: React.Dispatch<React.SetStateAction<boolean>>;
  applyDashboardFilters: () => void;
  showPublishedResultsOnly: () => void;
  resetWizard: () => void;
  error: string | JSX.Element;
  columnPresets: ColumnConfigurationPresetOption[];

  // data to render/getters
  serviceAreas: VolumeReportsState[];
  allServiceAreas: boolean;
  specialties: VolumeReportsSpecialty[];
  allSpecialties: boolean;
  projects: VolumeReportsProject[];
  tagLookup: TagLookup;
  isProjectWithNcpSelected: boolean;
  providerType: ProviderNetworkTypeEnum,
  appendNcp: boolean;
  networkProviderFilterType: NetworkProviderFilterType;
  impactProviderType: ExportImpactProviderTypeEnum;
  includePhysicianSpecialties: boolean;
  includeFacilitySpecialties: boolean;
  includeRequiredSpecialties: boolean;
  includeInformationalSpecialties: boolean;
  specialtyGapFilter: ExportGapFilterType;
  activeCounties: boolean;
  pendingCounties: boolean;
  approvedCounties: boolean;
  servicingType: CountiesFilterRadioOptionsType;
  specialtySummaryServicingTypes: CountiesFilterRadioOptionsType[];
  countiesGapsFilter: ExportGapFilterType;
  statesGapsFilter: ExportGapFilterType;
  reportName: string;
  reportFormat: ReportFormatOptionsType;
  reportFilter: ReportFilterOptionsType;
  alertWhenReady: boolean;
  splitReport: boolean;
  dashboardFilters: FilterItemsGroup[];
  publishedOnly: boolean;
  projectSortColumn: string;
  projectSortDirection: string;
  sharedUsers: number[];
  showZipSummariesOnly: boolean;
  includeCargoColumns: boolean;
  hasNetNewProviders: boolean;
  appendPatternOfCareProviders: boolean;
  membershipTypeFilter: MembershipType;
  includeInvalidCCIIORecords: boolean;
  selectedColumnPreset: ColumnConfigurationPresetOption;
  removeNonHSDColumns: boolean;
  zipClassification: string;
  zipClassOptions: string[];
  zipClassOptionsLoading: boolean;
  excludeZeroMembership: boolean;

  // setters
  setProviderType: React.Dispatch<React.SetStateAction<ProviderNetworkTypeEnum>>;
  setAppendNcp: React.Dispatch<React.SetStateAction<boolean>>;
  setNetworkProviderFilterType: React.Dispatch<React.SetStateAction<NetworkProviderFilterType>>;
  setImpactProviderType: React.Dispatch<React.SetStateAction<ExportImpactProviderTypeEnum>>;
  setIncludePhysicianSpecialties: React.Dispatch<React.SetStateAction<boolean>>;
  setIncludeFacilitySpecialties: React.Dispatch<React.SetStateAction<boolean>>;
  setIncludeRequiredSpecialties: React.Dispatch<React.SetStateAction<boolean>>;
  setIncludeInformationalSpecialties: React.Dispatch<React.SetStateAction<boolean>>;
  setSpecialtyGapFilter: React.Dispatch<React.SetStateAction<ExportGapFilterType>>;
  setActiveCounties: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingCounties: React.Dispatch<React.SetStateAction<boolean>>;
  setApprovedCounties: React.Dispatch<React.SetStateAction<boolean>>;
  setServicingType: React.Dispatch<React.SetStateAction<CountiesFilterRadioOptionsType>>;
  setSpecialtySummaryServicingTypes: React.Dispatch<React.SetStateAction<CountiesFilterRadioOptionsType[]>>;
  setCountiesGapsFilter: React.Dispatch<React.SetStateAction<ExportGapFilterType>>;
  setStatesGapsFilter: React.Dispatch<React.SetStateAction<ExportGapFilterType>>;
  setReportName: React.Dispatch<React.SetStateAction<string>>;
  setReportFormat: React.Dispatch<React.SetStateAction<ReportFormatOptionsType>>;
  setReportFilter: React.Dispatch<React.SetStateAction<ReportFilterOptionsType>>;
  setAlertWhenReady: React.Dispatch<React.SetStateAction<boolean>>;
  setSplitReport: React.Dispatch<React.SetStateAction<boolean>>;
  setSharedUsers: React.Dispatch<React.SetStateAction<number[]>>;
  setIncludeCargoColumns: React.Dispatch<React.SetStateAction<boolean>>;
  setAppendPatternOfCareProviders: React.Dispatch<React.SetStateAction<boolean>>;
  setRemoveNonHSDColumns: React.Dispatch<React.SetStateAction<boolean>>;
  setMembershipTypeFilter: React.Dispatch<React.SetStateAction<MembershipType>>;
  setIncludeInvalidCCIIORecords: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedColumnPreset: React.Dispatch<React.SetStateAction<ColumnConfigurationPresetOption>>;
  setZipClassification: React.Dispatch<React.SetStateAction<string>>;
  setExcludeZeroMembership: React.Dispatch<React.SetStateAction<boolean>>;

  // handlers
  updateProjectVisibility: (filter: string, isTag: boolean) => void;
  updateRegionVisibility: (filter: string) => void;
  updateSelectedProjects: (ids: string[]) => void;
  updateSelectedServiceArea: (state: string, county: string, checked: boolean) => void;
  updateSelectedSpecialty: (SpecialtyDisplayName: string, checked: boolean) => void;
  updateSpecialtiesVisibility: (filter: string) => void;
  sortProjects: (column: string) => void;
}

const VolumeReportsContext = createContext<Partial<VolumeReportsContextProps>>({});

const getDefaultReportName = (reportType: VolumeReportType) => {
  const now = moment();
  let reportTypeForTitle;
  switch (reportType) {
    case "specialty":
      reportTypeForTitle = "SpecialtySummaryByCounty"
      break;
    case "countyaggregate":
      reportTypeForTitle = "CountyAggregate";
      break;
    case "stateaggregate":
      reportTypeForTitle = "StateAggregate";
      break;
    case "specialtyaggregate":
      reportTypeForTitle = "SpecialtyAggregate";
      break;
    case "providers":
      reportTypeForTitle = "Providers";
      break;
    case "zip-summary":
      reportTypeForTitle = "ZipSummary"
      break;
    case "pci":
      reportTypeForTitle = "PciCrossProject"
      break;
    case "cciio":
      reportTypeForTitle = "CCIIOQHPNetwork"
      break;
    case "cciioDataValidations":
      reportTypeForTitle = "CCIIODataValidations"
      break;
    case "cciioDataValidationsSummary":
      reportTypeForTitle = "CCIIODataValidationsSummary"
      break;
    case "specialty-summary-zip-code":
      reportTypeForTitle = "SpecialtySummaryByZipCode";
      break;
    case "zip-class-aggregate":
      reportTypeForTitle = "ZipClassAggregateResults";
      break;
    case "zip-providers":
      reportTypeForTitle = "ZipProviders";
      break;
  }
  return `${reportTypeForTitle}VolumeReport_${now.format("YYYYMMDD[_]HHmmss")}`;
}

const VolumeReportsProvider = (props: VolumeReportsProps & { children: React.ReactNode }): JSX.Element => {
  const resourceFiltrationService = new ResourceFiltrationService();
  const exportService = useRef(new ExportService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;
  const serviceAreaHasChanged = useRef(false);
  const specialtiesHasChanged = useRef(false);
  const projectsHasChanged = useRef(false);
  const [showChangeWarning, setShowChangeWarning] = useState(false);
  const [tagLookup, setTagLookup] = useState<TagLookup>({});
  const [loading, setLoading] = useState(true);
  const [columnPresets, setColumnPresets] = useState<ColumnConfigurationPresetOption[]>(null);
  const [applyingDashboardFilters, setApplyingDashboardFilters] = useState(false);
  const [page, setPage] = useState<VolumeReportsWizardContextSteps>(null);
  const [error, setError] = useState<string | JSX.Element>("");
  const [publishedOnly, setPublishedOnly] = useState(!(useContext(UserContext).roles.includes(Roles.PREVIEW) || useContext(UserContext).roles.includes(Roles.RESOURCE_ADMIN)));
  const featureFlagsContext = useContext(FeatureFlagContext).enabledFeatureFlags;
  // for zip summaries we only want to show projects that have zip summaries enabled
  const showZipSummariesOnly = useRef(props.reportType === "zip-summary").current;
  // for zip project reports we only want to show projects that have zip project data
  const zipProjectsOnly = useRef(["specialty-summary-zip-code", "zip-class-aggregate", "zip-providers"].includes(props.reportType)).current;

  // step 1
  const [serviceAreas, setServiceAreas] = useState<VolumeReportsState[]>([]);
  const [allServiceAreas, setAllServiceAreas] = useState(true);

  // step 2
  const [specialties, setSpecialties] = useState<VolumeReportsSpecialty[]>([]);
  const [allSpecialties, setAllSpecialties] = useState(true);

  // step 3
  const [projects, setProjects] = useState<VolumeReportsProject[]>([]);
  const [isProjectWithNcpSelected, setIsProjectWithNcpSelected] = useState(false);
  const [hasNetNewProviders, setHasNetNewProviders] = useState(false);

  // step 4
  const [providerType, setProviderType] = useState<ProviderNetworkTypeEnum>(ProviderNetworkTypeEnum.Network);
  const [appendNcp, setAppendNcp] = useState(false);
  const [networkProviderFilterType, setNetworkProviderFilterType] = useState<NetworkProviderFilterType>(NetworkProviderFilterType.Available);
  const [impactProviderType, setImpactProviderType] = useState<ExportImpactProviderTypeEnum>(ExportImpactProviderTypeEnum.All);
  const [includePhysicianSpecialties, setIncludePhysicianSpecialties] = useState(true);
  const [includeFacilitySpecialties, setIncludeFacilitySpecialties] = useState(true);
  const [includeRequiredSpecialties, setIncludeRequiredSpecialties] = useState(true);
  const [includeInformationalSpecialties, setIncludeInformationalSpecialties] = useState(true);
  const [specialtyGapFilter, setSpecialtyGapFilter] = useState<ExportGapFilterType>(ExportGapFilterType.All);
  const [activeCounties, setActiveCounties] = useState(true);
  const [pendingCounties, setPendingCounties] = useState(true);
  const [approvedCounties, setApprovedCounties] = useState(true);
  const [servicingType, setServicingType] = useState<CountiesFilterRadioOptionsType>("Current Service Area");
  const [specialtySummaryServicingTypes, setSpecialtySummaryServicingTypes] = useState<CountiesFilterRadioOptionsType[]>(["Current Service Area"]);
  const [countiesGapsFilter, setCountiesGapsFilter] = useState<ExportGapFilterType>(ExportGapFilterType.All);
  const [statesGapsFilter, setStatesGapsFilter] = useState<ExportGapFilterType>(ExportGapFilterType.All);
  const [membershipTypeFilter, setMembershipTypeFilter] = useState<MembershipType>(MembershipType.Primary);
  const [selectedColumnPreset, setSelectedColumnPreset] = useState(defaultAllColumns);

  // step 5
  const [reportName, setReportName] = useState(getDefaultReportName(props.reportType));
  const [reportFormat, setReportFormat] = useState<ReportFormatOptionsType>(reportFormatOptions[0]);
  const [reportFilter, setReportFilter] = useState<ReportFilterOptionsType>("Default Format");
  const [alertWhenReady, setAlertWhenReady] = useState(true);
  const [splitReport, setSplitReport] = useState(false);
  const [includeCargoColumns, setIncludeCargoColumns] = useState(false);
  const [appendPatternOfCareProviders, setAppendPatternOfCareProviders] = useState(false);
  const [sharedUsers, setSharedUsers] = useState<number[]>([]);
  const [includeInvalidCCIIORecords, setIncludeInvalidCCIIORecords] = useState(false);

  const userId = useContext(UserContext).userId;
  const [dashboardFilters, setDashboardFilters] = useState<FilterItemsGroup[]>([]);
  const [projectSortColumn, setProjectSortColumn] = useState<string>("");
  const [projectSortDirection, setProjectSortDirection] = useState<string>("");
  const [removeNonHSDColumns, setRemoveNonHSDColumns] = useState(false);
  const [zipClassification, setZipClassification] = useState<string>("");
  const [zipClassOptions, setZipClassOptions] = useState<string[]>([]);
  const [zipClassOptionsLoading, setZipClassOptionsLoading] = useState(false);
  const [excludeZeroMembership, setExcludeZeroMembership] = useState(false);

  useEffect(() => {
    if (!["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType)) {
      getServiceAreas();
      setDashboardFilters(getDashboardFilters());
    }

    if (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType)) {
      setPage(VolumeReportsWizardContextSteps.SelectProjects);
    }
    else if (props.reportType === "specialtyaggregate") {
      setPage(VolumeReportsWizardContextSteps.SelectSpecialty);
    }
    else {
      setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
    }
  }, []);

  useEffect(() => {
    if (volumeReportTypeToReportTypeEnumMap.has(props.reportType) && featureFlagsContext.has(Features.ADEQUACY_REPORT_CUSTOMIZATION)) {
      exportService.getColumnCustomizationPresets(volumeReportTypeToReportTypeEnumMap.get(props.reportType)).then((presets) => {
        setColumnPresets([defaultAllColumns, ...presets]);
      })
    }
  }, [featureFlagsContext]);

  useEffect(() => {
    setNetworkProviderFilterType(NetworkProviderFilterType.Available);
    setImpactProviderType(ExportImpactProviderTypeEnum.All);
    setAppendNcp(false);
  }, [providerType]);

  useEffect(() => {
    resetFutureSteps(VolumeReportsWizardContextSteps.Filters);
  }, [ // this list should include all of the options in step 4 (Filters)
    providerType,
    appendNcp,
    NetworkProviderFilterType,
    impactProviderType,
    includePhysicianSpecialties,
    includeFacilitySpecialties,
    specialtyGapFilter,
    activeCounties,
    pendingCounties,
    approvedCounties,
    servicingType,
    specialtySummaryServicingTypes,
    countiesGapsFilter,
    statesGapsFilter,
    membershipTypeFilter,
    excludeZeroMembership
  ]);

  useEffect(() => {
    const newTagLookup = updateTagLookup(projects);
    setTagLookup(newTagLookup);
  }, [projects]);

  useEffect(() => {
    const hasNcp = projects.some((p) => p.isChecked && p.isNCPEnabled);
    setIsProjectWithNcpSelected(hasNcp);
  }, [projects]);

  useEffect(() => {
    const hasNetNew = projects.some((p) => p.isChecked && p.hasNetNewProviders);
    setHasNetNewProviders(hasNetNew);
  }, [projects]);

  const checkedProjectIdsKey = useMemo(
    () => projects.filter((p) => p.isChecked).map((p) => p.customProjectId).sort((a, b) => a - b).join(","),
    [projects]
  );

  useEffect(() => {
    if (props.reportType !== "zip-class-aggregate") {
      return;
    }
    let cancelled = false;
    const checkedProjectIds = checkedProjectIdsKey.split(",").filter(Boolean).map(Number);
    if (checkedProjectIds.length === 0) {
      setZipClassOptions([]);
      setZipClassOptionsLoading(false);
      return;
    }
    setZipClassOptionsLoading(true);
    zipProjectService.getZipClassAggregateFiltersForProjects(checkedProjectIds, publishedOnly).then((response) => {
      if (!cancelled) {
        setZipClassOptions(Array.isArray(response) ? response : []);
        setZipClassOptionsLoading(false);
      }
    }).catch(() => {
      if (!cancelled) {
        setZipClassOptions([]);
        setError("We were unable to retrieve the available ZIP classifications for the selected projects.");
        setZipClassOptionsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [checkedProjectIdsKey, publishedOnly]);

  useEffect(() => {
    switch (page) {
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        refreshFilteredSpecialties();
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        refreshFilteredProjects();
        break;
    }
  }, [page]);

  const getServiceAreas = (newPublishedOnly?: boolean) => {
    resourceFiltrationService.getStatesAndCounties(newPublishedOnly !== undefined ? newPublishedOnly : publishedOnly, showZipSummariesOnly, zipProjectsOnly).then((response: VolumeReportsState[]) => {
      response.forEach((state) => {
        state.checkboxState = CheckboxStateEnum.Unchecked;
        state.isVisible = true;
        state.childServiceAreas.forEach((county) => {
          county.isChecked = false;
          county.isVisible = true;
        });
      })
      setServiceAreas(response);
      setLoading(false);
    }).catch(() => {
      setError(<>We were unable to retrieve the service areas used by your projects. <QESButton qesType="secondary" className="p-0" qesTestId="volume-reports__refresh" onClick={() => { resetWizard() }}>Refresh</QESButton> to try again.</>);
    });
  };

  const getDashboardFilters = () => {
    const storedFilters: ResourceFilterSet = JSON.parse(window.localStorage.getItem(`${userId}project_filters`) || "{}");
    const combinedFilters = [...(storedFilters.filterGroups || []), ...(storedFilters.pendingChanges || [])];
    return combinedFilters;
  }

  const refreshFilteredSpecialties = () => {
    if (!serviceAreaHasChanged.current && !applyingDashboardFilters && specialties.length !== 0) {
      return;
    }
    setLoading(true);

    const handleSpecialtiesResponse = (response: FilterItem[]) => {
      setSpecialties(response.map((specialty) => ({
        displayName: specialty.displayName,
        isChecked: false,
        isVisible: true,
        namespacePath: specialty.namespacePath,
        specialtyGroupCode: specialty.itemValue
      })));
      serviceAreaHasChanged.current = false;
      setLoading(false);
    };

    if (serviceAreas.some((state) => state.checkboxState !== CheckboxStateEnum.Unchecked) && !serviceAreas.every((state) => state.checkboxState === CheckboxStateEnum.Checked)) {
      resourceFiltrationService.getFilterItemsInScope(makeParameterForSpecialties(), "§Specialties", true, publishedOnly, false, showZipSummariesOnly, zipProjectsOnly).then(handleSpecialtiesResponse).catch(() => {
        resourceFiltrationService.getFilterItems("§Specialties", true, publishedOnly, true, showZipSummariesOnly, zipProjectsOnly).then(handleSpecialtiesResponse).catch(() => {
          setError("We were unable to retrieve the list of specialty groups.");
        });
      });
    } else {
      resourceFiltrationService.getFilterItems("§Specialties", true, publishedOnly, true, showZipSummariesOnly, zipProjectsOnly).then(handleSpecialtiesResponse).catch(() => {
        setError("We were unable to retrieve the list of specialty groups.");
      });
    }
  }

  const getServiceAreaFilters = () => {
    const serviceAreaFilters: FilterSetItem[] = serviceAreas.reduce<FilterSetItem[]>((result, currentState) => {
      if (currentState.checkboxState === CheckboxStateEnum.Checked) {
        result.push({ displayName: currentState.displayName, namespacePath: currentState.namespacePath });
      } else if (currentState.checkboxState === CheckboxStateEnum.Indeterminate) {
        result
          .push(...currentState.childServiceAreas.reduce<FilterSetItem[]>((countyResult, currentCounty) => {
            if (currentCounty.isChecked) {
              countyResult.push({ displayName: currentCounty.displayName, namespacePath: currentCounty.namespacePath })
            }
            return countyResult;
          }, []));
      }
      return result;
    }, []);
    return serviceAreaFilters;
  }

  const refreshFilteredProjects = () => {
    if (!serviceAreaHasChanged.current && !specialtiesHasChanged.current && !applyingDashboardFilters && projects.length !== 0) {
      return;
    }
    setLoading(true);

    resourceFiltrationService.getVolumeReportingProjects(makeParameterForProjects(), props.reportType !== "pci" ? publishedOnly : true, showZipSummariesOnly, props.reportType === "pci" ? true : false,
      ["cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType) ? true : false, props.reportType === "cciio" ? true : false, zipProjectsOnly).then((projectResponse) => {
        setProjects(projectResponse.map((project) => ({ ...project, isVisible: true })));
        serviceAreaHasChanged.current = false;
        specialtiesHasChanged.current = false;
        setLoading(false);
      }).catch(() => {
        setError("We were unable to retrieve the filtered list of projects.")
      });
  }

  const updateTagLookup = (newProjectList: VolumeReportsProject[]) => {
    const newTagLookup: TagLookup = {};
    const allTags = [...new Set<string>(newProjectList.flatMap((p) => p.tags))].filter((tag) => tag !== undefined);
    allTags.forEach((t) => {
      const projects: VolumeReportsProject[] = [];
      newProjectList.forEach((p) => {
        if (p.tags?.includes(t)) {
          projects.push(p);
        }
      })
      newTagLookup[t] = projects;
    });

    return newTagLookup;
  }

  const updateSelectedServiceArea = (stateDisplayName: string, countyDisplayName: string, checked: boolean) => {
    const updatedList: VolumeReportsState[] = structuredClone(serviceAreas);
    const state: VolumeReportsState = updatedList.find((item) => item.displayName === stateDisplayName);
    if (stateDisplayName) {
      if (!countyDisplayName) {
        // select/unselect all VISIBLE counties in a state, and set state to checked/unchecked/indeterminate
        const visibleCounties = state.childServiceAreas.filter((child) => child.isVisible);
        const shouldUncheck = visibleCounties.every((county) => county.isChecked);
        visibleCounties.forEach((child) => {
          child.isChecked = shouldUncheck ? false : checked;
        });
      } else {
        // just get the one county
        state.childServiceAreas.find((child) => child.displayName === countyDisplayName).isChecked = checked;
      }
      const checkedCounties = state.childServiceAreas.filter((child) => child.isChecked);
      // set checked/unchecked indeterminate based on ALL counties, not just visible
      state.checkboxState = state.childServiceAreas.length === checkedCounties.length ? CheckboxStateEnum.Checked :
        checkedCounties.length === 0 ? CheckboxStateEnum.Unchecked :
          CheckboxStateEnum.Indeterminate;
    } else {
      // if neither is provided, do all visible
      updatedList.forEach((state) => {
        if (state.isVisible) {
          state.childServiceAreas.forEach((county) => {
            if (county.isVisible) county.isChecked = checked;
          })
          const checkedCounties = state.childServiceAreas.filter((child) => child.isChecked);
          state.checkboxState = state.childServiceAreas.length === checkedCounties.length ? CheckboxStateEnum.Checked :
            checkedCounties.length === 0 ? CheckboxStateEnum.Unchecked :
              CheckboxStateEnum.Indeterminate;
        }
      })
    }
    setServiceAreas(updatedList);
    setAllServiceAreas(updatedList.every((servicearea) => servicearea.checkboxState == CheckboxStateEnum.Checked) || updatedList.every((serviceArea) => serviceArea.checkboxState == CheckboxStateEnum.Unchecked))
    resetFutureSteps(VolumeReportsWizardContextSteps.SelectServiceArea);
  }

  const updateSelectedSpecialty = (specialtyDisplayName: string, checked: boolean) => {
    const updatedList: VolumeReportsSpecialty[] = structuredClone(specialties);
    const filteredSpecialties = updatedList.filter((specialty) => specialty.isVisible);
    if (specialtyDisplayName) {
      updatedList.find((sp) => sp.displayName === specialtyDisplayName).isChecked = checked;
    } else {
      const shouldUncheck = filteredSpecialties.every((specialty) => specialty.isChecked);
      filteredSpecialties.forEach((sp) => {
        sp.isChecked = shouldUncheck ? false : checked;
      })
    }
    setAllSpecialties(updatedList.every((specialty) => specialty.isChecked) || updatedList.every((specialty) => !specialty.isChecked))
    specialtiesHasChanged.current = true;
    setSpecialties(updatedList);
    resetFutureSteps(VolumeReportsWizardContextSteps.SelectSpecialty);
  }

  const updateSelectedProjects = (ids: string[]) => {
    const newProjects: VolumeReportsProject[] = structuredClone(projects);
    newProjects.forEach((project) => {
      if (ids.includes(project.customProjectId.toString())) {
        project.isChecked = true;
      } else {
        project.isChecked = false;
      }
    });
    projectsHasChanged.current = true;
    resetFutureSteps(VolumeReportsWizardContextSteps.SelectProjects);
    setProjects(newProjects);
  }

  const updateRegionVisibility = (filterString: string) => {
    const updatedList: VolumeReportsState[] = structuredClone(serviceAreas);
    if (!filterString) {
      updatedList.forEach((state) => {
        state.isVisible = true;
        state.childServiceAreas.forEach((child) => {
          child.isVisible = true;
        })
      })
    } else {
      const regex = new RegExp(filterString, "i");
      updatedList.forEach((state) => {
        let hasMatchingCounty = false;
        state.childServiceAreas.forEach((county) => {
          const isMatch = !!county.displayName.match(regex);
          county.isVisible = isMatch;
          hasMatchingCounty = isMatch || hasMatchingCounty;
        });
        state.isVisible = hasMatchingCounty;
      });
    }
    setServiceAreas(updatedList);
  }

  const updateSpecialtiesVisibility = (filterString: string) => {
    const updatedList: VolumeReportsSpecialty[] = structuredClone(specialties);
    if (!filterString) {
      updatedList.forEach((sp) => {
        sp.isVisible = true
      });
    } else {
      const regex = new RegExp(filterString, "i");
      updatedList.forEach((sp) => {
        sp.isVisible = !!sp.displayName.match(regex);
      });
    }
    setSpecialties(updatedList);
  }

  const updateProjectVisibility = (filterString: string, isTag: boolean) => {
    const newProjects: VolumeReportsProject[] = structuredClone(projects);
    const regex = new RegExp(scrubUserInput(filterString), "i");
    if (isTag && filterString !== "") {
      newProjects.forEach((p) => {
        p.isVisible = !!p.tags?.join(" ").match(regex);
      })
    } else {
      newProjects.forEach((p) => {
        p.isVisible = !!p.projectName.match(regex);
      })
    }
    setProjects(newProjects);
  }

  const sortProjects = (column: string) => {

    let sortDirection: 'asc' | 'desc' = 'asc';
    if (column === projectSortColumn) {
      sortDirection = projectSortDirection === 'asc' ? 'desc' : 'asc';
      setProjectSortDirection(sortDirection);
    }
    else {
      setProjectSortColumn(column);
      setProjectSortDirection(sortDirection);
    }

    const multiplier = sortDirection === 'asc' ? 1 : -1;

    const newProjects = [...projects];

    switch (column) {
      case 'projectName':
        setProjects(newProjects.sort((a, b) => (a.projectName.toLocaleLowerCase() > b.projectName.toLocaleLowerCase() ? 1 : -1) * multiplier));
        break;
      case 'gapCount':
        setProjects(newProjects.sort((a, b) => (a.gapCount > b.gapCount ? 1 : -1) * multiplier));
        break;
      case 'templateName':
        setProjects(newProjects.sort((a, b) => (a.templateName.toLocaleLowerCase() > b.templateName.toLocaleLowerCase() ? 1 : -1) * multiplier));
        break;
      default:
        break;
    }
  }

  const resetFutureSteps = (currentStep: VolumeReportsWizardContextSteps) => {
    function resetSpecialties() {
      setSpecialties([]);
      serviceAreaHasChanged.current = true;
    }
    function resetProjects() {
      setProjects([]);
    }
    function resetFilters() {
      setProviderType(ProviderNetworkTypeEnum.Network);
      setAppendNcp(false);
      setNetworkProviderFilterType(NetworkProviderFilterType.Available);
      setImpactProviderType(ExportImpactProviderTypeEnum.All);
      setIncludePhysicianSpecialties(true);
      setIncludeFacilitySpecialties(true);
      setIncludeRequiredSpecialties(true);
      setIncludeInformationalSpecialties(true);
      setSpecialtyGapFilter(ExportGapFilterType.All);
      setActiveCounties(true);
      setPendingCounties(true);
      setApprovedCounties(true);
      setServicingType("Current Service Area");
      setSpecialtySummaryServicingTypes(["Current Service Area"]);
      setCountiesGapsFilter(ExportGapFilterType.All);
      setStatesGapsFilter(ExportGapFilterType.All);
      setMembershipTypeFilter(MembershipType.Primary);
      setZipClassification("");
      setExcludeZeroMembership(false);
    }
    function resetConfig() {
      setAppendPatternOfCareProviders(false);
    }
    switch (currentStep) {
      case VolumeReportsWizardContextSteps.SelectServiceArea:
        resetSpecialties();
        resetProjects();
        resetFilters();
        resetConfig();
        break;
      case VolumeReportsWizardContextSteps.SelectSpecialty:
        resetProjects();
        resetFilters();
        resetConfig();
        break;
      case VolumeReportsWizardContextSteps.SelectProjects:
        resetFilters();
        resetConfig();
        break;
      case VolumeReportsWizardContextSteps.Filters:
        break;
    }
    setShowChangeWarning(false);
  }

  const applyDashboardFilters = async () => {
    const freshDashboardFilters = getDashboardFilters();
    setLoading(true);
    setDashboardFilters(freshDashboardFilters);
    setApplyingDashboardFilters(true);
    setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
    /** 
     * 1. we don't need to worry about dashboard filters existing because the button won't be there if they don't
     *   use the dashboard filters to get the list of resources
     * 2. we COULD just get the list of resources and jump straight to Filters, but we need to populate the service area and
     *  specialties pages
     */
    // We start in step 1, and that's currently service areas
    // Fun fact: You can make the § symbol in Windows with alt + numpad7, alt + numpad8, alt + numpad 9
    /**
       * The project dashboard filters allow entire states to be ORed together, or to be ANDed. We'll opt to OR all of them here.
       * For counties, it only allows counties from the same state to be ORed together, so we'll do that and AND groups of counties from different states
       * For clarification, the operation parameter in result.counties dictates the internal behavior of the filters parameter. The API ANDs 
       * every set of filters, regardless of the operation parameter. This functionality is all based on the dashboard filters implementation.
       */
    const serviceAreaFilters = freshDashboardFilters.reduce((previous, current) => {
      if (current.filters[0].namespacePath.includes("§Counties")) {
        previous.counties.push(...current.filters.map((f) => f.namespacePath));
      } else if (current.filters[0].namespacePath.includes("§States§")) {
        previous.states.push(...current.filters.map((f) => f.namespacePath));
      }
      return previous;
    }, { counties: [], states: [] });

    const newServiceAreas = serviceAreas.map((state) => {
      if (serviceAreaFilters.states.includes(state.namespacePath)) {
        state.checkboxState = CheckboxStateEnum.Checked;
        state.childServiceAreas = state.childServiceAreas.map((csa) => ({ ...csa, isChecked: true }));
      } else {
        if (state.childServiceAreas.some((csa) => serviceAreaFilters.counties.includes(csa.namespacePath))) {
          state.checkboxState = CheckboxStateEnum.Indeterminate;
          state.childServiceAreas = state.childServiceAreas.map((csa) => ({ ...csa, isChecked: serviceAreaFilters.counties.includes(csa.namespacePath) }));
        } else {
          // ensure if the user had other selections, they're removed
          state.checkboxState = CheckboxStateEnum.Unchecked;
          state.childServiceAreas = state.childServiceAreas.map((csa) => ({ ...csa, isChecked: false }));
        }
      }
      return state;
    });

    setServiceAreas(newServiceAreas);
    serviceAreaHasChanged.current = false;

    // Specialties and Projects
    let specialties: FilterItem[] = [];
    if (serviceAreaFilters.counties.length > 0 || serviceAreaFilters.states.length > 0) {
      specialties = await resourceFiltrationService.getFilterItemsInScope(makeParameterForSpecialties(), "§Specialties", true, publishedOnly, false, showZipSummariesOnly, zipProjectsOnly);
    } else {
      specialties = await resourceFiltrationService.getFilterItems("§Specialties", true, publishedOnly, true, showZipSummariesOnly, zipProjectsOnly);
    }

    const specialtiesFilters = freshDashboardFilters
      .reduce((previous, current) => {
        if (current.filters[0].namespacePath.includes("§Specialties§")) {
          previous.push(...current.filters.map((f) => f.namespacePath));
        }
        return previous;
      }, []);
    const newSpecialties: VolumeReportsSpecialty[] = specialties.map((specialty) => {
      const isIncluded = specialtiesFilters.includes(specialty.namespacePath);
      return {
        ...specialty,
        isChecked: isIncluded,
        isVisible: true,
        specialtyGroupCode: specialty.itemValue
      }
    });
    setSpecialties(newSpecialties);

    try {
      const filteredProjects = await resourceFiltrationService.getFilteredProjectResources(freshDashboardFilters);
      const resourceIds = filteredProjects.map((resource) => resource.resourceId);
      const volumeReportingProjects = await resourceFiltrationService.getVolumeReportingProjects(makeParameterForProjects(), props.reportType !== "pci" ? publishedOnly : true, showZipSummariesOnly, props.reportType === "pci" ? true : false,
        ["cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType) ? true : false, props.reportType === "cciio" ? true : false, zipProjectsOnly);

      const newProjects = volumeReportingProjects.map((p) => {
        const isIncluded = resourceIds.includes(p.customProjectId);
        return {
          ...p,
          isChecked: isIncluded,
          isVisible: isIncluded,
        }
      });

      setProjects(newProjects);
      setLoading(false);
      setApplyingDashboardFilters(false);
      setShowChangeWarning(true);

      if (!newProjects.some((project) => project.isChecked)) {
        toast("No projects with the applied filters are able to run reports", {
          autoClose: false,
          containerId: "z-index-1030",
          type: "error",
        });
        resetWizard();
      }
      else {
        toast("Your Project Dashboard Filters were successfully applied!", {
          autoClose: false,
          containerId: "z-index-1030",
          type: "success",
        });
      }
    } catch {
      setError("We were unable to apply your dashboard filters.");
    }
  }

  const makeParameterForSpecialties = () => {
    const stateAndCountyFilters = getServiceAreaFilters();
    const filterItemsGroup: FilterItemsGroup[] = [
      {
        filters: stateAndCountyFilters,
        operation: FilterItemsGroupOperation.LogicalOr,
      }
    ];
    return filterItemsGroup;
  }

  const showPublishedResultsOnly = () => {
    const newPublishedOnly = !publishedOnly;
    const isProjectFirstReport = ["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType);
    setPublishedOnly(newPublishedOnly);
    if (!isProjectFirstReport) getServiceAreas(newPublishedOnly);
    resetWizard();

    if (["cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType)) {
      setLoading(true);
      resourceFiltrationService.getVolumeReportingProjects(makeParameterForProjects(), newPublishedOnly, showZipSummariesOnly, false,
        ["cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType) ? true : false, props.reportType === "cciio" ? true : false, zipProjectsOnly).then((projectResponse) => {
          setProjects(projectResponse.map((project) => ({ ...project, isVisible: true })));
          setLoading(false);
        }).catch(() => {
          setError("We were unable to retrieve the filtered list of projects.");
        });
    }

    const message = newPublishedOnly
      ? "Only showing published results."
      : "Showing unpublished results.";

    toast(message, {
      containerId: "z-index-1030",
      type: "success",
    });
  }

  const makeParameterForProjects = () => {
    const serviceAreaFilters = getServiceAreaFilters();
    const serviceAreaStrings = serviceAreaFilters.map((stateOrCounty) => stateOrCounty.namespacePath);
    const specialtiesFilterStrings = specialties.every((sp) => sp.isChecked) ? [] : specialties.filter((sp) => sp.isChecked).map((sp) => (sp.namespacePath));

    const filterSetItems: FilterItemsGroupModel[] = [];
    serviceAreaStrings.length > 0 && filterSetItems.push({ filterItems: serviceAreaStrings, operation: FilterItemsGroupOperation.LogicalOr });
    if (specialtiesFilterStrings.length > 0) {
      filterSetItems.push({
        filterItems: specialtiesFilterStrings,
        operation: FilterItemsGroupOperation.LogicalOr,
      });
    }
    return filterSetItems;
  }

  const resetWizard = () => {
    const isProjectFirstReport = ["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType);
    if (serviceAreas.length > 0) {
      const newServiceAreas = serviceAreas.map((state) => {
        const counties = state.childServiceAreas.map((county) => ({
          ...county,
          isChecked: false,
        }));
        return {
          ...state,
          checkboxState: CheckboxStateEnum.Unchecked,
          childServiceAreas: counties,
        };
      });
      setServiceAreas(newServiceAreas);
    } else if (!isProjectFirstReport) {
      getServiceAreas();
    }

    if (["pci", "cciio", "cciioDataValidationsSummary", "cciioDataValidations"].includes(props.reportType)) {
      setPage(VolumeReportsWizardContextSteps.SelectProjects);
      resetFutureSteps(VolumeReportsWizardContextSteps.SelectProjects);
    }
    else if (props.reportType === "specialtyaggregate") {
      setPage(VolumeReportsWizardContextSteps.SelectSpecialty);
      resetFutureSteps(VolumeReportsWizardContextSteps.SelectSpecialty);
    }
    else {
      setPage(VolumeReportsWizardContextSteps.SelectServiceArea);
      resetFutureSteps(VolumeReportsWizardContextSteps.SelectServiceArea);
    }
    setError("");
  }

  const value = {
    activeCounties,
    alertWhenReady,
    allServiceAreas,
    allSpecialties,
    appendNcp,
    appendPatternOfCareProviders,
    applyDashboardFilters,
    approvedCounties,
    columnPresets,
    countiesGapsFilter,
    dashboardFilters,
    error,
    excludeZeroMembership,
    hasNetNewProviders,
    impactProviderType,
    includeCargoColumns,
    includeFacilitySpecialties,
    includeInformationalSpecialties,
    includeInvalidCCIIORecords,
    includePhysicianSpecialties,
    includeRequiredSpecialties,
    isProjectWithNcpSelected,
    loading,
    membershipTypeFilter,
    networkProviderFilterType,
    page,
    pendingCounties,
    projects,
    projectSortColumn,
    projectSortDirection,
    providerType,
    publishedOnly,
    removeNonHSDColumns,
    reportFilter,
    reportFormat,
    reportName,
    reportType: props.reportType,
    resetWizard,
    selectedColumnPreset,
    serviceAreas,
    servicingType,
    setActiveCounties,
    setAlertWhenReady,
    setAllServiceAreas,
    setAllSpecialties,
    setAppendNcp,
    setAppendPatternOfCareProviders,
    setApprovedCounties,
    setCountiesGapsFilter,
    setExcludeZeroMembership,
    setImpactProviderType,
    setIncludeCargoColumns,
    setIncludeFacilitySpecialties,
    setIncludeInformationalSpecialties,
    setIncludeInvalidCCIIORecords,
    setIncludePhysicianSpecialties,
    setIncludeRequiredSpecialties,
    setMembershipTypeFilter,
    setNetworkProviderFilterType,
    setPage,
    setPendingCounties,
    setProviderType,
    setPublishedOnly,
    setRemoveNonHSDColumns,
    setReportFilter,
    setReportFormat,
    setReportName,
    setSelectedColumnPreset,
    setServicingType,
    setSharedUsers,
    setShowChangeWarning,
    setSpecialtyGapFilter,
    setSpecialtySummaryServicingTypes,
    setSplitReport,
    setStatesGapsFilter,
    setZipClassification,
    sharedUsers,
    showChangeWarning,
    showPublishedResultsOnly,
    sortProjects,
    specialties,
    specialtyGapFilter,
    specialtySummaryServicingTypes,
    splitReport,
    statesGapsFilter,
    tagLookup,
    updateProjectVisibility,
    updateRegionVisibility,
    updateSelectedProjects,
    updateSelectedServiceArea,
    updateSelectedSpecialty,
    updateSpecialtiesVisibility,
    zipClassification,
    zipClassOptions,
    zipClassOptionsLoading,

  };

  return (
    <VolumeReportsContext.Provider value={value}>
      {props.children}
    </VolumeReportsContext.Provider>
  );
};

export { VolumeReportsContext, VolumeReportsProvider };
